import { create } from "zustand";
import { persist } from "zustand/middleware";

interface User {
  id: number;
  mail: string;
  isAdmin: boolean;
  firstName: string;
  lastName: string;
}

interface AuthState {
  token: string | null;
  user: User | null;
  setToken: (token: string | null) => void;
  setUser: (user: User | null) => void;
  logout: () => void;
}

// This store is the single owner of the session token, which lives in two
// places: `auth-storage` (this store, via persist) and the raw `jwtToken`
// localStorage key that `src/api.ts` reads for the bearer header on every
// request. They are not derived from each other, so `setToken` and `logout`
// write both — deliberately, because the alternative was making every caller
// remember to pair `logout()` with `localStorage.removeItem("jwtToken")`, and
// one of the six call sites forgot, leaving a valid token on disk after logout.
//
// Keep it that way: no code outside this file should touch the `jwtToken` key
// except `api.ts`, which only reads it.
export const useAuthStore = create(
  persist<AuthState>(
    (set) => ({
      token: null as string | null,
      user: null as User | null,
      setToken: (token: string | null) => {
        if (token) {
          localStorage.setItem("jwtToken", token);
        } else {
          localStorage.removeItem("jwtToken");
        }
        set({ token });
      },
      setUser: (user: User | null) => set({ user }),
      logout: () => {
        localStorage.removeItem("jwtToken");
        set({ token: null, user: null });
      },
    }),
    {
      name: "auth-storage",
    },
  ),
);
